/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable import/no-extraneous-dependencies */
import React from 'react';
import { Upload, message } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { DraggerProps } from './types';

const OOXML_SIGNATURE = [0x50, 0x4b, 0x03, 0x04];
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const isBrowserFile = (file: unknown): file is File => typeof File !== 'undefined' && file instanceof File;

const hasOoxmlSignature = async (file: File) => {
  const bytes = new Uint8Array(
    await file.slice(0, OOXML_SIGNATURE.length).arrayBuffer(),
  );
  return OOXML_SIGNATURE.every((byte, index) => bytes[index] === byte);
};

const normalizeExcelFileName = async (file: UploadRequestOption['file']) => {
  if (!isBrowserFile(file) || !file.name.toLowerCase().endsWith('.xls')) return file;
  if (!(await hasOoxmlSignature(file))) return file;

  return new File([file], file.name.replace(/\.xls$/i, '.xlsx'), {
    type: XLSX_MIME_TYPE,
    lastModified: file.lastModified,
  });
};

const Dragger = ({
  fileList,
  setFileList,
  uploadFile,
  extraValidation,
  mimeTypes,
  children,
  ...props
}: DraggerProps) => {
  const { Dragger: AntDragger } = Upload;
  const { t } = useTranslation();
  // eslint-disable-next-line consistent-return
  const beforeUpload = (file: UploadFile) => {
    if (!mimeTypes.includes(file.type as string) && file.name) {
      message.error(t('utils.uploads.unsupportedFile', { document: file.name }));
      return false;
    }
    if (extraValidation && !extraValidation(file)) return false;
    if (props.multiple && file) setFileList((prevState: UploadFile[]) => [...prevState, file]);
    if (!props.multiple && file) setFileList([file]);
  };

  const onRemove = (file: UploadFile) => {
    const index = fileList.indexOf(file);
    const newFileList = fileList.slice();
    newFileList.splice(index, 1);
    setFileList(newFileList);
  };

  // eslint-disable-next-line consistent-return
  const onChange = (options: UploadChangeParam<UploadFile>) => {
    const { status } = options.file;

    if (status === 'done') {
      message.success(
        t('utils.uploads.uploadSuccess', { document: options.file.name }),
      );
    } else if (status === 'error') {
      message.error(
        t('utils.uploads.uploadFailed', { document: options.file.name }),
      );
    }
  };

  const customRequest = async (options: UploadRequestOption) => {
    const { onSuccess, onError, file } = options;
    const formData = new FormData();
    try {
      const normalizedFile = await normalizeExcelFileName(file);
      formData.append('file', normalizedFile);
      if (uploadFile) {
        await uploadFile(formData);
      }
      onSuccess?.('Ok');
    } catch (err) {
      const error = new Error('Something went wrong');
      onError?.(error);
    }
  };
  return (
    <AntDragger
      customRequest={customRequest}
      onChange={onChange}
      fileList={fileList}
      onRemove={onRemove}
      beforeUpload={beforeUpload}
      {...props}
    >
      {children || (
        <>
          <UploadOutlined className="mr-2" />
          {t('worker.excelSheet.uploadFile')}
        </>
      )}
    </AntDragger>
  );
};

export default Dragger;
